"use client"

import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts"

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"

const YEARS = 25

const generateSavingsData = () => {
  const data = []
  let annualWithout = 1200
  let annualWith = 360
  let cumulativeWithout = 0
  let cumulativeWith = 0

  for (let year = 1; year <= YEARS; year++) {
    cumulativeWithout += annualWithout
    cumulativeWith += annualWith

    data.push({
      year: `Año ${year}`,
      sinPaneles: Math.round(cumulativeWithout),
      conPaneles: Math.round(cumulativeWith),
    })

    annualWithout *= 1.04
    annualWith *= 1.015
  }

  return data
}

const chartData = generateSavingsData()

const chartConfig = {
  sinPaneles: {
    label: "Sin paneles",
    color: "#ef4444",
  },
  conPaneles: {
    label: "Con paneles",
    color: "#76CBCA",
  },
} satisfies ChartConfig

const formatEuro = (value: number) =>
  `${Math.round(value).toLocaleString("es-ES")} €`

export function SavingsChart() {
  return (
    <Card className="pt-0">
      <CardHeader className="flex items-center gap-2 space-y-0 border-b py-5">
        <div className="grid flex-1 gap-1">
          <CardTitle>Sin paneles vs Con paneles</CardTitle>
          <CardDescription>
            Coste acumulado de energía en 25 años (ilustrativo)
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
        <ChartContainer
          config={chartConfig}
          className="aspect-auto h-[250px] w-full"
        >
          <AreaChart
            data={chartData}
            margin={{ left: 12, right: 12 }}
          >
            <defs>
              <linearGradient id="fillSinPaneles" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor="var(--color-sinPaneles)"
                  stopOpacity={0.5}
                />
                <stop
                  offset="95%"
                  stopColor="var(--color-sinPaneles)"
                  stopOpacity={0.05}
                />
              </linearGradient>
              <linearGradient id="fillConPaneles" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor="var(--color-conPaneles)"
                  stopOpacity={0.6}
                />
                <stop
                  offset="95%"
                  stopColor="var(--color-conPaneles)"
                  stopOpacity={0.05}
                />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="year"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={24}
              interval={4}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={(value) => `${Math.round(Number(value) / 1000)}k€`}
            />
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  labelFormatter={(value) => `Año ${value.replace("Año ", "")}`}
                  formatter={(value) => formatEuro(Number(value))}
                  indicator="dot"
                />
              }
            />
            <Area
              dataKey="sinPaneles"
              type="monotone"
              fill="url(#fillSinPaneles)"
              stroke="var(--color-sinPaneles)"
              stackId="a"
            />
            <Area
              dataKey="conPaneles"
              type="monotone"
              fill="url(#fillConPaneles)"
              stroke="var(--color-conPaneles)"
              stackId="a"
            />
            <ChartLegend content={<ChartLegendContent />} />
          </AreaChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}
